import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import PDFViewer from '../../components/PDFViewer';
import axios from 'axios';
import { Helmet } from "react-helmet-async";
import Preloader from '../../components/Preloader/Preloader';
import { useSnackbar } from 'notistack';
import { BookOpen, FileText, Sparkles, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';

const config = require('../../config.json');

export default function Tatavaviveka() {
  const [collapsed, setCollapsed] = useState(true);
  const [selectedItem, setSelectedItem] = useState('');
  const [pdfFile, setPdfFile] = useState('');
  const [loaded, setLoaded] = useState(true);
  const [searchResult, setSearchResult] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchPublicationList();
  }, []);

  const fetchPublicationList = async () => {
    try {
      setLoaded(false);
      const fetchOption = "PubCategory";
      const fetchKey = "Active";
      const fetchCategory = "Tattvaviveka";
      const response = await axios.get(
        `${config.api.invokeURL}/dashboard/publications?fetchOption=${fetchOption}&fetchKey=${fetchKey}&fetchCategory=${fetchCategory}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );
      setSearchResult(response.data.Items || []);
      if (response.data.Items && response.data.Items.length > 0) {
        enqueueSnackbar(`${response.data.Items.length} active records found in Tattvaviveka`, { variant: 'success' });
      }
      setLoaded(true);
      window.scrollTo(0, 0);
    } catch (error) {
      enqueueSnackbar(`Error occurred: ${error.message}`, { variant: 'warning' });
      setLoaded(true);
    }
  };

  const toggle = () => {
    setCollapsed(!collapsed);
  };

  const handlePDFViewer = async (event, item) => {
    if (event) event.preventDefault();
    setLoaded(false);
    setSelectedItem(item);
    try {
      const fetchFile = item.id + ".pdf";
      const bucketName = 'docs.vidyajyoticollege.in/pdfs/vjtoday';
      const response = await axios.get(
        `${config.api.invokeURL}/presigned/static?fileName=${fetchFile}&bucketName=${bucketName}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );
      setPdfFile(response.data.downloadURL);
      setCollapsed(false);
      setLoaded(true);
    } catch (error) {
      enqueueSnackbar(`Unable to load PDF: ${error.message}`, { variant: 'error' });
      setLoaded(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>Tattvaviveka - Vidyajyoti College of Theology - Delhi</title>
        <meta name="description" content="Tattvaviveka - Philosophical and Theological Journal of Vidyajyoti" />
        <meta name="keywords" content="Tattvaviveka, Vidyajyoti publications, Indian philosophy, Theology journal" />
        <link rel="canonical" href="/publications/tattvaviveka" />
      </Helmet>

      <Preloader show={!loaded} />

      {/* PDF Modal */}
      <Modal fullscreen isOpen={!collapsed} toggle={toggle} style={{ top: '0%' }}>
        <ModalHeader
          className="d-flex justify-content-between align-items-center"
          style={{ background: '#1a2b56', color: '#fff' }}
          close={
            <button className="btn btn-sm btn-outline-light rounded-pill px-3" onClick={toggle}>
              ✕ Close
            </button>
          }
        >
          <span className="fw-bold">{selectedItem ? (selectedItem.issue || selectedItem.title || 'Tattvaviveka Issue') : 'Journal Viewer'}</span>
        </ModalHeader>
        <ModalBody className="p-0">
          <PDFViewer pdfFile={pdfFile} />
        </ModalBody>
      </Modal>

      <div className="py-5" style={{ background: '#f8fafc', minHeight: '80vh' }}>
        <div className="container">
          {/* Page Header */}
          <div className="text-center mx-auto pb-4" style={{ maxWidth: '750px' }}>
            <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(198, 146, 20, 0.12)', color: '#c69214', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(198, 146, 20, 0.25)' }}>
              <Sparkles size={14} /> Academic Journal
            </div>
            <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
              Tattvaviveka
            </h1>
            <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
              A specialized journal exploring Indian philosophical heritage, theological discernment, subaltern reflections, and inter-religious dialogue published under the aegis of Vidyajyoti Faculty of Theology.
            </p>
          </div>

          {/* Journal Grid */}
          <div className="row g-4 justify-content-center">
            {searchResult && searchResult.length > 0 ? (
              searchResult.map((item, index) => (
                <motion.div
                  key={index}
                  className="col-sm-6 col-md-4 col-lg-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <div
                    className="card h-100 border-0 shadow-sm rounded-4 p-4 text-center bg-white cursor-pointer position-relative overflow-hidden"
                    style={{
                      transition: 'all 0.3s ease',
                      borderTop: '4px solid #79031d',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => handlePDFViewer(e, item)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                    }}
                  >
                    <div className="d-inline-flex p-3 rounded-circle mb-3 mx-auto" style={{ background: 'rgba(26, 43, 86, 0.06)', color: '#1a2b56' }}>
                      <BookOpen size={28} />
                    </div>

                    <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>
                      {item.issue || 'Issue Edition'}
                    </h5>

                    {item.title && item.title !== item.issue && (
                      <p className="text-muted small mb-3">{item.title}</p>
                    )}

                    <div className="mt-auto pt-3 border-top">
                      <button 
                        className="btn btn-sm w-100 rounded-pill fw-semibold d-flex align-items-center justify-content-center gap-2"
                        style={{ background: '#79031d', color: '#fff', border: 'none' }}
                      >
                        <FileText size={15} /> Read Issue <ExternalLink size={13} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              loaded && (
                <div className="col-12 text-center py-5">
                  <div className="p-4 rounded-4 bg-white shadow-sm d-inline-block" style={{ maxWidth: '450px' }}>
                    <BookOpen size={36} className="text-muted mb-2" />
                    <h5 className="fw-semibold text-muted">No issues currently listed</h5>
                    <p className="small text-muted mb-0">Archived volumes will appear here once loaded from the repository.</p>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
}
